import { useMemo } from 'react';
import type { BasicHealthcareEntity } from '../../../../js/api/models/BasicHealthcareEntity';
import { createGoogleMapsLink } from '../../../../js/utils/address';
import { Button } from '../../components/button';
import { TooltippedText } from '../../components/tooltipped-text';
import { DynamicFieldFactory } from '../../utils/DynamicFieldFactory';
import { FacilityContactsBlock } from './FacilityContactsBlock';

export type FacilityDetailsPanelModel = {
  street_address?: string | null;
  street_address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  phone_number?: string | null;
  timezone?: string | null;
  hospital_ownership?: string | null;
  emr?: string | null;
  extra_info?: string | null;
  integration_code?: string | null;
  facility_type_fields?: any[] | null;
  healthcare_entity?: BasicHealthcareEntity | null;
};

export type FacilityDetailsPanelProps = {
  facility: FacilityDetailsPanelModel;
  /** Maps from useCodeDefinitionsByCategories → code → title (e.g. HospitalOwnershipChoices) */
  codeDefMaps: Record<string, Record<string, string>>;
  canEdit?: boolean;
  onEditClick?: () => void;
  /** When set, shows facility contacts (list + add/edit/delete) using this facility id. */
  facilityId?: string;
};

/**
 * Facility address, ownership, EMR, notes, dynamic type fields, and optional contacts
 * (when `facilityId` is set). Shared by FacilityDrawer, OpportunityDrawer, etc.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- layout branches (address, edit buttons, dynamic fields)
export function FacilityDetailsPanel({
  facility,
  codeDefMaps,
  canEdit = false,
  onEditClick,
  facilityId,
}: FacilityDetailsPanelProps) {
  const showEdit = Boolean(canEdit && onEditClick);

  const hasAddressBlock =
    facility.street_address ||
    facility.street_address2 ||
    facility.city ||
    facility.state ||
    facility.zip_code;

  const dynamicFields = useMemo(
    () => facility.facility_type_fields ?? [],
    [facility.facility_type_fields]
  );

  return (
    <>
      <section className="tw:mb-4">
        <div className="tw:mb-12">
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-8 tw:gap-y-6">
            <div className="tw:space-y-6">
              <div className="tw:flex tw:items-start tw:justify-between">
                <div>
                  <div className="tw:text-zinc-400 tw:text-sm tw:flex tw:items-center tw:gap-2">
                    Address
                    {facility.street_address && (
                      <a
                        href={createGoogleMapsLink(
                          facility.street_address,
                          facility.city ?? undefined,
                          facility.state ?? undefined,
                          facility.zip_code ?? undefined
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in Google Maps"
                      >
                        <i className="fa-solid fa-location-dot" />
                      </a>
                    )}
                  </div>
                  <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                    {facility.street_address && <div>{facility.street_address}</div>}
                    {facility.street_address2 && <div>{facility.street_address2}</div>}
                    {(facility.city || facility.state || facility.zip_code) && (
                      <div>
                        {facility.city}
                        {facility.city && facility.state ? ', ' : ''}
                        {facility.state}
                        {(facility.city || facility.state) && facility.zip_code ? ' ' : ''}
                        {facility.zip_code}
                      </div>
                    )}
                    {!hasAddressBlock && <div>--</div>}
                    <div>
                      {facility.phone_number ? (
                        <a
                          href={`tel:${facility.phone_number}`}
                          className="tw:hover:underline"
                          title={`Call ${facility.phone_number}`}
                        >
                          {facility.phone_number}
                        </a>
                      ) : (
                        '--'
                      )}
                    </div>
                    {facility.timezone && (
                      <div className="tw:text-xs tw:text-zinc-500 tw:font-light tw:italic tw:mt-1">
                        <i className="fa-regular fa-clock tw:mr-1" />
                        {facility.timezone}
                      </div>
                    )}
                  </div>
                </div>
                {showEdit && (
                  <Button
                    className="tw:inline-flex tw:md:!hidden"
                    color="brand-green-outline"
                    onClick={onEditClick}
                  >
                    <i className="fa-solid fa-pen-to-square" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
            <div className="tw:space-y-6 tw:relative">
              <div className="tw:flex tw:justify-between tw:items-start">
                <div className="tw:space-y-6">
                  <div>
                    <div className="tw:text-zinc-400 tw:text-sm">Ownership</div>
                    <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                      {codeDefMaps['HospitalOwnershipChoices']?.[
                        facility.hospital_ownership ?? ''
                      ] || '--'}
                    </div>
                  </div>
                  <div>
                    <div className="tw:text-zinc-400 tw:text-sm">EMR System</div>
                    <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                      {codeDefMaps['EMRChoices']?.[facility.emr ?? ''] || '--'}
                    </div>
                  </div>
                  <div>
                    <div className="tw:text-zinc-400 tw:text-sm tw:flex tw:items-center tw:gap-1">
                      Integration ID
                      <TooltippedText
                        tooltipContent="Organization code to use for integration processes."
                        spanClassName="tw:pl-1"
                      >
                        <i className="far fa-info-circle tw:cursor-pointer" />
                      </TooltippedText>
                    </div>
                    <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                      {facility.integration_code || '--'}
                    </div>
                  </div>
                </div>
                {showEdit && (
                  <Button
                    className="tw:!hidden tw:md:!inline-flex"
                    color="brand-green-outline"
                    onClick={onEditClick}
                  >
                    <i className="fa-solid fa-pen-to-square" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </div>
          {facility.extra_info && (
            <div className="tw:mt-4">
              <div className="tw:text-zinc-400 tw:text-sm">Additional Info</div>
              <div className="tw:text-zinc-700 tw:dark:text-zinc-300 tw:mt-1 tw:whitespace-pre-line">
                {facility.extra_info}
              </div>
            </div>
          )}
          {dynamicFields.length > 0 && (
            <div className="tw:mt-4">
              <DynamicFieldFactory
                fields={dynamicFields}
                columns={2}
                onChange={() => {}}
                readOnly={true}
              />
            </div>
          )}
        </div>
      </section>
      {facilityId ? (
        <FacilityContactsBlock
          facilityId={facilityId}
          facilityName={facility.name}
          healthcareEntityId={facility.healthcare_entity?.id}
          canEdit={canEdit}
          roleMap={codeDefMaps['FacilityContactRoles']}
        />
      ) : null}
    </>
  );
}
